import { useActivateDealMutation } from '@src/components/OMS/ManualDealTradeView/endpoints/activateDealEndpoint';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import type { OTCDealData } from '@src/types/OTC';
import { isForwardDeal, isOptionDeal } from '@src/types/OTC/otcGuards';
import { isDealActivatable, isDealCancelable, isDealModifiable } from '@src/utils/OTC/otcUtils';
import {
  ACTION,
  agGridMenuSeparator,
  filterByCellValueMenuItem,
  IconName,
  useCallbackRef,
  useJsonModal,
  useSecuritiesContext,
  type AgGridIconButtonProps,
  type AgGridMenuItems,
  type Column,
  type ColumnDef,
  type FilterableProperty,
  type MixpanelInstance,
  type Security,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, ICellRendererParams, MenuItemDef } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import { useCancelDealDialog } from '../CancelDealDialog';
import { createDealJsonObject, getDealJsonObject } from '../dealJsonObject';
import { useModifyDealMenuAction } from '../useModifyDealMenuAction';
import { OTCGridRowMenu } from './OTCGridRowMenu';
import { colIDToFilterBuilderKey } from './useOTCDerivativesFilter';

interface UseOTCMenuParams {
  openClause?: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties?: FilterableProperty[];
  mixpanel: MixpanelInstance;
  /** Opens OTC derivative instrument inspector; must be stable (e.g. from parent `useCallback`). */
  openOTCInstrumentInspectorDrawer: (deal: OTCDealData, onlyIfOpen?: boolean) => void;
}

export function useOTCDerivativesMenu({
  openClause,
  filterableProperties,
  mixpanel,
  openOTCInstrumentInspectorDrawer,
}: UseOTCMenuParams): {
  columns: Column[];
  getCustomContextMenuItems: (params: GetContextMenuItemsParams<OTCDealData>) => AgGridMenuItems;
  dataDisplayCallback: (data: OTCDealData) => unknown;
  dialogs: ReactNode;
} {
  const { handleClickJson, jsonModal } = useJsonModal<{ deal: OTCDealData; security: Security | undefined }>();
  const { securitiesBySymbol } = useSecuritiesContext();
  const handleCancelDeal = useCancelDealDialog();
  const [activateDeal] = useActivateDealMutation();
  const { isAuthorized } = useRoleAuth();
  const canSubmitDeal = isAuthorized(ACTION.SUBMIT_DEAL);
  const canCancelDeal = isAuthorized(ACTION.CANCEL_DEAL);
  const { handleModifyDeal, modifyDealDialog } = useModifyDealMenuAction();

  const createCustomJsonObject = useCallbackRef((deal?: OTCDealData) => {
    createDealJsonObject({
      deal,
      propertyName: 'deal',
      securitiesBySymbol,
      handleClickJson,
    });
  });

  const dataDisplayCallback = useCallbackRef((deal: OTCDealData) =>
    getDealJsonObject({
      deal,
      propertyName: 'deal',
      securitiesBySymbol,
    })
  );

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<OTCDealData>) => {
    const data = params.node?.data;

    const filterItems: AgGridMenuItems = [];
    if (openClause) {
      const filterMenuItems = filterByCellValueMenuItem({
        params,
        filterableProperties: filterableProperties || [],
        openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      });
      filterItems.push(
        ...filterMenuItems.filter(
          (item): item is MenuItemDef<OTCDealData> => item !== agGridMenuSeparator && item !== 'separator'
        )
      );
    }
    const menuItems: AgGridMenuItems = compact([
      ...filterItems,
      ...(filterItems.length > 0 ? [agGridMenuSeparator] : []),
      {
        name: 'View Instrument Details',
        action: () => {
          if (data) {
            openOTCInstrumentInspectorDrawer(data);
          }
        },
        icon: `<i class="ag-icon ${IconName.InformationCircle}"/>`,
      },
    ]);

    const isModifiable = data && isDealModifiable(data);
    const isCancelable = data && isDealCancelable(data);
    const canModifyOTCDerivative = data && isModifiable && (isForwardDeal(data) || isOptionDeal(data));
    const isActivatable = data && isDealActivatable(data) && data.ID != null;

    const secondaryActions: MenuItemDef[] = [];
    if (canModifyOTCDerivative && canSubmitDeal) {
      secondaryActions.push({
        name: 'Modify Booking',
        action: () => {
          if (data) {
            handleModifyDeal(data);
          }
        },
        icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
      });
    }
    if (isCancelable && canCancelDeal) {
      secondaryActions.push({
        name: 'Cancel Deal',
        action: () => {
          handleCancelDeal(data);
        },
        icon: `<i class="ag-icon ${IconName.Trash}"/>`,
      });
    }
    if (isActivatable && canSubmitDeal) {
      secondaryActions.push({
        name: 'Activate Deal',
        action: () => {
          if (data?.ID != null) {
            void activateDeal({ dealId: data.ID });
          }
        },
        icon: `<i class="ag-icon ${IconName.CheckCircle}"/>`,
      });
    }
    if (secondaryActions.length > 0) {
      menuItems.push(agGridMenuSeparator, ...secondaryActions);
    }

    return menuItems;
  });

  const renderOTCMenuItems = useCallbackRef((params: ICellRendererParams<OTCDealData>) => (
    <OTCGridRowMenu {...params} onShowJson={createCustomJsonObject} />
  ));

  const columns = useMemo(
    () =>
      [
        // Show More Details button (pinned to the right, before hamburger menu)
        {
          type: 'iconButton',
          id: 'show-more-details',
          frozen: true,
          pinned: 'right',
          suppressColumnsToolPanel: true,
          width: 40,
          params: {
            icon: IconName.Deepdive,
            onClick: params => {
              const data: OTCDealData | undefined = params.node.data;
              if (data) {
                openOTCInstrumentInspectorDrawer(data);
              }
            },
          } satisfies AgGridIconButtonProps,
        },
        {
          type: 'hamburgerMenu',
          id: 'otcMenu',
          width: 40,
          params: {
            renderItems: renderOTCMenuItems,
          },
        },
      ] satisfies ColumnDef<OTCDealData>[],
    [openOTCInstrumentInspectorDrawer, renderOTCMenuItems]
  );

  const dialogs = useMemo(
    () => (
      <>
        {jsonModal}
        {modifyDealDialog}
      </>
    ),
    [jsonModal, modifyDealDialog]
  );

  return {
    columns,
    getCustomContextMenuItems,
    dataDisplayCallback,
    dialogs,
  };
}
